/**
 * AdminDashboard Page
 * Main analytics dashboard for administrators with real-time metrics
 * Uses Analytics API endpoints with date range and client filters
 */

import { useState } from 'react';
import { AdminPageLayout } from '@/components/admin/shared/AdminPageLayout';
import { DateRangePicker, DateRange } from '@/components/admin/dashboard/DateRangePicker';
import { ClientFilter } from '@/components/admin/dashboard/ClientFilter';
import { GlobalMetricsSection } from '@/components/admin/dashboard/GlobalMetricsSection';
import { TrendsSection } from '@/components/admin/dashboard/TrendsSection';
import { VoicebotQualitySection } from '@/components/admin/dashboard/VoicebotQualitySection';
import { ChatbotQualitySection } from '@/components/admin/dashboard/ChatbotQualitySection';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { getDateRangeFromPreset, formatAnalyticsDate, calculateDaysBetween } from '@/hooks/useAnalytics';
import { BarChart3, TrendingUp, Phone, MessageSquare, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useQueryClient } from '@tanstack/react-query';

function AdminDashboard() {
  const queryClient = useQueryClient();

  // Initialize with last 30 days as default
  const [dateRange, setDateRange] = useState<DateRange>(() => getDateRangeFromPreset('30d'));
  const [selectedClientId, setSelectedClientId] = useState<string | undefined>(undefined);

  // Build params for hooks
  const analyticsParams = {
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    clientId: selectedClientId,
  };

  const handleRefresh = () => {
    // Invalidate all analytics queries to force refetch
    queryClient.invalidateQueries({ queryKey: ['analytics'] });
  };

  const dayCount = calculateDaysBetween(dateRange.startDate, dateRange.endDate);

  return (
    <AdminPageLayout
      title="Analytics Dashboard"
      subtitle={`${formatAnalyticsDate(dateRange.startDate)} - ${formatAnalyticsDate(dateRange.endDate)} (${dayCount} days)`}
      actions={
        <Button onClick={handleRefresh} variant="outline" size="sm" className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Refresh Data
        </Button>
      }
    >
      <div className="space-y-6">
        {/* Filters Section */}
        <Card className="p-4">
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="flex-1">
              <DateRangePicker value={dateRange} onChange={setDateRange} />
            </div>
            <div className="lg:w-[280px]">
              <ClientFilter value={selectedClientId} onChange={setSelectedClientId} />
            </div>
          </div>
        </Card>

        {/* Global Metrics Section */}
        <div>
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Global Metrics Overview
          </h2>
          <GlobalMetricsSection dateRange={analyticsParams} />
        </div>

        {/* Tabbed Sections for Trends and Quality Analysis */}
        <Tabs defaultValue="trends" className="space-y-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="trends" className="gap-2">
              <TrendingUp className="h-4 w-4" />
              Trends
            </TabsTrigger>
            <TabsTrigger value="voicebot" className="gap-2">
              <Phone className="h-4 w-4" />
              Voicebot Quality
            </TabsTrigger>
            <TabsTrigger value="chatbot" className="gap-2">
              <MessageSquare className="h-4 w-4" />
              Chatbot Quality
            </TabsTrigger>
          </TabsList>

          {/* Trends Tab */}
          <TabsContent value="trends" className="space-y-4">
            <div>
              <h3 className="text-md font-semibold mb-3">Metrics Trends</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Visualize daily trends for voicebot costs, calls, chatbot messages, and leads
              </p>
              <TrendsSection dateRange={analyticsParams} />
            </div>
          </TabsContent>

          {/* Voicebot Quality Tab */}
          <TabsContent value="voicebot" className="space-y-4">
            <div>
              <h3 className="text-md font-semibold mb-3">Voicebot Quality Analysis</h3>
              <p className="text-sm text-muted-foreground mb-4">
                AI-generated quality analysis for voicebot calls with success scores, outcomes, and sentiment
              </p>
              <VoicebotQualitySection dateRange={analyticsParams} />
            </div>
          </TabsContent>

          {/* Chatbot Quality Tab */}
          <TabsContent value="chatbot" className="space-y-4">
            <div>
              <h3 className="text-md font-semibold mb-3">Chatbot Quality Analysis</h3>
              <p className="text-sm text-muted-foreground mb-4">
                AI-generated quality analysis for chatbot conversations with quality scores, resolution status, and engagement
              </p>
              <ChatbotQualitySection dateRange={analyticsParams} />
            </div>
          </TabsContent>
        </Tabs>

        {/* Data Availability Note */}
        <Card className="p-4 bg-blue-50 border-blue-200">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <svg
                className="h-5 w-5 text-blue-600"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-blue-900 mb-1">Data Availability</h4>
              <p className="text-sm text-blue-800">
                Metrics are aggregated daily at 2:00 AM. Quality analysis is generated weekly on Sundays at 3:00 AM by
                AI workflows. If you see empty sections, data may still be processing or awaiting the next workflow run.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </AdminPageLayout>
  );
}

// Export both named and default for compatibility
export { AdminDashboard };
export default AdminDashboard;
